// cspell: ignore undoable
import { NgClass } from "@angular/common";
import { Component, Input, OnInit, inject } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ClassDirective } from "@ngbracket/ngx-layout/extended";
import { FlexDirective, LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { ToastService } from "@tylertech/core-module";
import { IChartCriteria } from "@tylertech/tyler-api-types";
import { ForgeCardModule, ForgeDividerModule, ForgeIconButtonModule, ForgeIconModule, ForgeLinearProgressModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { IntentService } from "@tylertech/intents-module";
import { PacketService } from "@tylertech/packets-module";
import { TodoItemDTO } from "../../interfaces";
import { UBDashboardService } from "../../services";
import { AddTodoItemDialogComponent } from "../dialogs/addTodoItemDialog.component";

// TODO - this ain't no chart....
@Component({
    selector: "utilities-todo-card",
    templateUrl: "./utilitiesTodoCard.tmpl.html",
    styleUrls: [
        "./utilitiesTodoCard.component.scss"
    ],
    imports: [
        ClassDirective,
        FlexDirective,
        LayoutAlignDirective,
        LayoutDirective,
        ForgeCardModule,
        ForgeDividerModule,
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeLinearProgressModule,
        ForgeTooltipModule,
        NgClass,
    ]
})
export class UtilitiesTodoCardComponent implements OnInit
{
    private readonly $toastService = inject(ToastService);
    private readonly $ubDashboardService = inject(UBDashboardService);
    private readonly matDialog = inject(MatDialog);
    private readonly $intentService = inject(IntentService);
    private readonly $packetService = inject(PacketService);

    // Unused - the card loads its own items; accepted so it can be bound like the other dashboard charts.
    @Input() chartCriteria: IChartCriteria;

    initialCount: number = 5;

    isSaving: boolean = false;

    items: TodoItemDTO[] = [];

    showCount: number;

    private undoableItems: TodoItemDTO[] = [];

    ngOnInit(): void
    {
        this.loadItems();
    }

    get canUndo(): boolean
    {
        return this.undoableItems?.length !== 0;
    }

    get displayItems(): Array<TodoItemDTO>
    {
        return this.items.slice(0, this.showCount);
    }

    get canShowMore(): boolean
    {
        return this.items.length > this.showCount;
    }

    get hasItems(): boolean
    {
        return (this.items?.length || 0) !== 0;
    }

    isCustom(item: TodoItemDTO): boolean
    {
        return (item.EntityType === "Custom");
    }

    addTodo(): void
    {
        const dialogRef = this.matDialog.open(AddTodoItemDialogComponent);
        dialogRef.afterClosed().toPromise()
            .then((result: TodoItemDTO) =>
            {
                if (!result)
                {
                    return;
                }

                this.items.push(result);
                this.loadItems();
            });
    }

    completed(item: TodoItemDTO): void
    {
        item.Saving = true;
        item.Completed = true;
        this.$ubDashboardService.saveTodoItem(item)
            .then(_result =>
            {
                item.Saving = false;
                this.undoableItems.push(item);
                this.$toastService.showToast("Todo Item Completed");
                this.filterCompleted();
            });
    }

    private filterCompleted(): void
    {
        this.items = this.items.filter(x => !x.Completed);
    }

    private loadItems(): void
    {
        const self = this;
        this.$ubDashboardService.getTodoItems()
            .then(result =>
            {
                self.items = result;
                self.order();
                this.setShowCount(this.initialCount);
            });
    }

    openEntity(item: TodoItemDTO): void
    {
        switch (item.EntityType)
        {
            case "UB.Account": {
                this.$intentService.resolve("OpenUBAccount", "Open", { id: item.EntityId });
                break;
            }
            case "UB.ServiceOrder": {
                this.$intentService.resolve("OpenUBServiceOrder", "Open", { id: item.EntityId });
                break;
            }
            case "CC.Packet": {
                this.$packetService.GetProcessIdByPacketId(item.EntityId)
                    .then((processId: string) =>
                    {
                        this.$intentService.resolve("OpenProcessing", "Open", { ProcessId: processId });
                    });
                break;
            }
            default: {
                break;
            }
        }
    }

    pastDue(item: TodoItemDTO): string
    {
        const answer = new Date(item.DueDate) < new Date(Date.now()) ? "red-font" : "";
        return answer;
    }

    order(): void
    {
        this.items.sort((a1, a2) =>
        {
            if (new Date(a1.DueDate) > new Date(a2.DueDate))
            {
                return 1;
            }
            else if (new Date(a1.DueDate) < new Date(a2.DueDate))
            {
                return -1;
            }
            return 0;
        });
    }

    getClass(item: TodoItemDTO): Record<string, boolean>
    {
        /* eslint-disable @typescript-eslint/naming-convention */
        return {
            "red-font": new Date(item.DueDate) <= new Date()
        };
        /* eslint-enable @typescript-eslint/naming-convention */
    }

    setShowCount(count: number): void
    {
        this.showCount = count;
        if ((this.items?.length || 0) < this.showCount)
        {
            this.showCount = this.items.length;
        }
    }

    showMore($event?: MouseEvent): void
    {
        if ($event !== undefined)
        {
            $event.stopPropagation();
        }
        this.setShowCount(this.showCount + this.initialCount);
    }

    undo(): void
    {
        const self = this;
        const item = this.undoableItems.pop();
        item.Completed = false;
        this.$ubDashboardService.saveTodoItem(item)
            .then(_result =>
            {
                self.items.push(item);
                self.filterCompleted();
                self.order();
            });
    }

    getAriaLabel(item: TodoItemDTO): string
    {
        return `Submission-Complete-${item.EntityNumber}-ToDo-UtilityBilling`;
    }
}
